/**
 * SOLID Review
 * S — Strong. One concern: execute the Ads domain's single query and unwrap it.
 * O — Strong. A second ads operation is one more method and one more document.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Implements a one-method port.
 * D — Strong. Takes IPortGraphQLDomainClient — the narrow executor from this domain's socket.
 */
/**
 * DDD Layer: Infrastructure — the Ads half of the client↔server seam.
 *
 * The executor arrives from THIS domain's socket on ComponentFrontendInfrastructure
 * (`graphQLAdsDomainPort`). Before this adapter existed there was no consumer of that socket at
 * all: the query was an inline `gql` in AppRoot.tsx driven by `useLazyQuery`.
 */
import { gql } from '@apollo/client';
import type { IAdapterApolloAds, IEntityAdProviderConfig } from '../../application/ads/ports/IAdapterApolloAds';
import type { IPortGraphQLDomainClient } from '../contract/ports';

const GET_AD_PROVIDER_CONFIG_QUERY = gql`
  query ApolloAdsAdapter_GetAdProviderConfig($playerId: ID!) {
    getAdProviderConfig(playerId: $playerId) {
      provider
      shouldShowAds
      refreshIntervalSeconds
      propellerZoneId
      adsensePublisherId
      adsenseSlotId
    }
  }
`;

export class ApolloAdsAdapter implements IAdapterApolloAds {
  constructor(private readonly client: IPortGraphQLDomainClient) {}

  async fetchAdProviderConfig(playerId: string): Promise<IEntityAdProviderConfig | null> {
    // `network-only` preserved from the useLazyQuery this replaced: the A/B assignment is resolved
    // once per session by the backend, and a cached answer would pin a returning player to a stale
    // arm of the experiment.
    const { data } = await this.client.request((apollo) => apollo.query<{ getAdProviderConfig: IEntityAdProviderConfig | null }>({
      query: GET_AD_PROVIDER_CONFIG_QUERY,
      variables: { playerId },
      fetchPolicy: 'network-only',
    }));
    return data?.getAdProviderConfig ?? null;
  }
}
